import type { WidgetConfig } from '@trinity/chat/types/dashboard-config';
import type { CanonicalWidgetData } from '@trinity/chat/types/dashboard-data';
import { CanonicalWidgetDataSchema } from '@trinity/chat/types/dashboard-data';
import { widgetDataRequestFromSource, type WidgetDataProxy } from '@trinity/chat/types/widget-data-proxy';

// ─── Core Interface ───────────────────────────────────────────────────────────

/** Per-fetch context. Overrides what the provider was constructed with. */
export interface DataFetchContext {
  /** Brand sent with remote requests, in place of the provider's own. */
  brandIdentifier?: string;
  /** The application `brandIdentifier` is used in; a connection request with a brand needs it. */
  applicationIdentifier?: string;
}

/**
 * DataProvider — the abstraction layer that decouples data retrieval from
 * widget rendering.
 *
 * ⚠️ Data population is deliberately undetermined. The exact sources that feed
 * widgets are TBD and vary by use case. Any data source (HTTP agent, static
 * JSON, REST API, DB query, CSV, CRM, etc.) implements this interface without
 * touching widget code, the builder, or the renderer.
 */
export interface DataProvider {
  /**
   * Fetch canonical data for a single widget.
   * Return `null` to indicate no data available (widget shows placeholder).
   */
  fetchWidgetData(
    widgetId: string,
    config: WidgetConfig,
    context?: DataFetchContext,
  ): Promise<CanonicalWidgetData | null>;

  /**
   * Fetch canonical data for all widgets in bulk.
   * The returned map may be partial — missing widgetIds render as empty.
   */
  fetchAllWidgetData(
    widgets: WidgetConfig[],
    context?: DataFetchContext,
  ): Promise<Map<string, CanonicalWidgetData>>;
}

// ─── StaticDataProvider ───────────────────────────────────────────────────────

/**
 * Returns hardcoded preview data passed in at construction time.
 * Used for builder preview mode and templates where no live source exists.
 */
export class StaticDataProvider implements DataProvider {
  constructor(
    private readonly staticData: Map<string, CanonicalWidgetData> = new Map(),
  ) {}

  async fetchWidgetData(
    widgetId: string,
    _config: WidgetConfig,
    _context?: DataFetchContext,
  ): Promise<CanonicalWidgetData | null> {
    return this.staticData.get(widgetId) ?? null;
  }

  async fetchAllWidgetData(
    widgets: WidgetConfig[],
    _context?: DataFetchContext,
  ): Promise<Map<string, CanonicalWidgetData>> {
    const result = new Map<string, CanonicalWidgetData>();
    for (const widget of widgets) {
      const data = this.staticData.get(widget.id);
      if (data) result.set(widget.id, data);
    }
    return result;
  }
}

// ─── CompositeDataProvider ────────────────────────────────────────────────────

/**
 * Chains multiple providers with first-match wins.
 * e.g. `new CompositeDataProvider([snapshotProvider, staticProvider])`
 * tries the snapshot first; falls back to static if no data found.
 */
export class CompositeDataProvider implements DataProvider {
  constructor(private readonly providers: DataProvider[]) {}

  async fetchWidgetData(
    widgetId: string,
    config: WidgetConfig,
    context?: DataFetchContext,
  ): Promise<CanonicalWidgetData | null> {
    for (const provider of this.providers) {
      const data = await provider.fetchWidgetData(widgetId, config, context);
      if (data !== null) return data;
    }
    return null;
  }

  async fetchAllWidgetData(
    widgets: WidgetConfig[],
    context?: DataFetchContext,
  ): Promise<Map<string, CanonicalWidgetData>> {
    const result = new Map<string, CanonicalWidgetData>();
    const remaining = new Set(widgets.map(w => w.id));

    for (const provider of this.providers) {
      if (remaining.size === 0) break;
      const remainingWidgets = widgets.filter(w => remaining.has(w.id));
      const partial = await provider.fetchAllWidgetData(remainingWidgets, context);
      for (const [widgetId, data] of partial) {
        result.set(widgetId, data);
        remaining.delete(widgetId);
      }
    }

    return result;
  }
}

// ─── ConfigAwareDataProvider ──────────────────────────────────────────────────

/**
 * The primary runtime data provider.
 *
 * For each widget it inspects `widget.dataSource`:
 * - `static` (or absent): returns `dataSource.data` directly (canonical data is
 *   stored in the data source by the builder/config tools).
 * - `connection` or `agent`: calls the supplied `proxyRequest` function (backend proxy) and
 *   returns the parsed response as CanonicalWidgetData.
 *
 * The `proxyRequest` dependency is injected so each app can point at its own
 * backend proxy without the provider knowing any app-specific details.
 */
export class ConfigAwareDataProvider implements DataProvider {
  constructor(
    private readonly proxyRequest: WidgetDataProxy,
    private readonly brandIdentifier?: string,
  ) {}

  async fetchWidgetData(
    widgetId: string,
    config: WidgetConfig,
    context?: DataFetchContext,
  ): Promise<CanonicalWidgetData | null> {
    const source = config.dataSource;
    if (!source) return null;

    // static — canonical data is stored directly in dataSource.data (already validated at save time)
    if (source.type === 'static') {
      if (!source.data) return null;
      return source.data as CanonicalWidgetData;
    }

    // A source that isn't picked yet (builder drafts) has nothing to fetch.
    if (source.type === 'agent' && !source.agentId) return null;
    if (source.type === 'connection' && !source.connectionIdentifier) return null;

    const request = widgetDataRequestFromSource(source);
    if (!request) return null;

    try {
      const raw = await this.proxyRequest({
        ...request,
        brandIdentifier: context?.brandIdentifier ?? this.brandIdentifier,
        applicationIdentifier: context?.applicationIdentifier,
      });
      const parsed = CanonicalWidgetDataSchema.safeParse(raw);
      // Safe cast: safeParse validated the data matches the schema; the hand-written
      // CanonicalWidgetData type mirrors the schema but with stricter required fields.
      return parsed.success ? (parsed.data as CanonicalWidgetData) : null;
    } catch {
      return null;
    }
  }

  async fetchAllWidgetData(
    widgets: WidgetConfig[],
    context?: DataFetchContext,
  ): Promise<Map<string, CanonicalWidgetData>> {
    const entries = await Promise.all(
      widgets.map(async w => [w.id, await this.fetchWidgetData(w.id, w, context)] as const),
    );
    const result = new Map<string, CanonicalWidgetData>();
    for (const [id, data] of entries) {
      if (data !== null) result.set(id, data);
    }
    return result;
  }
}
